import React, { useState, useEffect, useRef } from "react";
import { Plus, Edit2, Trash2, Package, Clock, Tag, Layers, HelpCircle } from "lucide-react";
import { addonService } from "../../../services/addonService";
import billingService from "../../../services/billingService";
import { spaceManagementService } from "../../../services/host-apiServices/spaceManagementService";
import { showApiMessage } from "../../../utils/messageHandler";
import { formatDurationType } from "../../../utils/bookingTimeframeUtils";
import { useUserCurrency } from "../../../hooks/useUserCurrency";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../shadcn/ui/dialog";
import { Button } from "../../../shadcn/ui/button";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { Textarea } from "../../../shadcn/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { Checkbox } from "../../../shadcn/ui/checkbox";
import Pagination from "../../../components/Pagination";
import logger from "../../../utils/logger";

const DAYS_LIST = [
  { key: "MONDAY", label: "Monday" },
  { key: "TUESDAY", label: "Tuesday" },
  { key: "WEDNESDAY", label: "Wednesday" },
  { key: "THURSDAY", label: "Thursday" },
  { key: "FRIDAY", label: "Friday" },
  { key: "SATURDAY", label: "Saturday" },
  { key: "SUNDAY", label: "Sunday" },
];

const DEFAULT_FORM_DATA = {
  name: "",
  description: "",
  unitPrice: "",
  type: "NON_SUBSCRIPTION",
  serviceCategoryId: "",
  serviceType: "",
  applicableUserCategories: ["HOST", "B2B"],
  duration: "HOURLY",
  maximumValidity: 24,
  baseDuration: 1,
  numberOfInstances: 1,
  scheduleMode: "ALL_SLOTS", // "ALL_SLOTS" (unconstrained) or "CUSTOM_SLOTS"
  slotsByDay: {
    MONDAY: { enabled: true, startTime: "09:00", endTime: "17:00" },
    TUESDAY: { enabled: true, startTime: "09:00", endTime: "17:00" },
    WEDNESDAY: { enabled: true, startTime: "09:00", endTime: "17:00" },
    THURSDAY: { enabled: true, startTime: "09:00", endTime: "17:00" },
    FRIDAY: { enabled: true, startTime: "09:00", endTime: "17:00" },
    SATURDAY: { enabled: false, startTime: "09:00", endTime: "17:00" },
    SUNDAY: { enabled: false, startTime: "09:00", endTime: "17:00" },
  },
};

const AddonsSettings = () => {
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [addonToDelete, setAddonToDelete] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
    totalElements: 0,
    totalPages: 0,
  });
  const userCurrency = useUserCurrency();

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  // Convert time string "HH:MM" or "HH:MM:SS" or object -> API wall-clock string "HH:mm:ss"
  const parseTimeString = timeStr => {
    if (!timeStr) return "00:00:00";
    if (typeof timeStr === "string") {
      const parts = timeStr.trim().split(":");
      const h = String(parts[0] || 0).padStart(2, "0");
      const m = String(parts[1] || 0).padStart(2, "0");
      const s = String(parts[2] || 0).padStart(2, "0");
      return `${h}:${m}:${s}`;
    }
    if (typeof timeStr === "object") {
      const h = String(timeStr.hour || 0).padStart(2, "0");
      const m = String(timeStr.minute || 0).padStart(2, "0");
      const s = String(timeStr.second || 0).padStart(2, "0");
      return `${h}:${m}:${s}`;
    }
    return "00:00:00";
  };

  // Convert time object or string to "HH:MM"
  const formatTimeSlotDisplay = timeVal => {
    if (!timeVal) return "00:00";
    if (typeof timeVal === "string") return timeVal.slice(0, 5);
    if (typeof timeVal === "object") {
      const h = String(timeVal.hour || 0).padStart(2, "0");
      const m = String(timeVal.minute || 0).padStart(2, "0");
      return `${h}:${m}`;
    }
    return "00:00";
  };

  useEffect(() => {
    fetchAddons();
    fetchServiceCategories();
  }, []);

  const fetchServiceCategories = async () => {
    try {
      setIsLoadingCategories(true);
      const response = await billingService.getServiceCategories({ limit: 100 });
      if (response.status === 200) {
        setServiceCategories(response.data?.data || []);
      }
    } catch (error) {
      logger.error("Error fetching service categories:", error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const fetchAddons = async (page = 1, size = 10) => {
    try {
      setLoading(true);
      const response = await addonService.getMyOrganizationAddons({ page: page - 1, size: size });
      if (response.status === 200) {
        setAddons(response.data.content || response.data || []);
        if (response.data.content) {
          setPagination({
            page: page,
            size: size,
            totalElements: response.data.totalElements || 0,
            totalPages: response.data.totalPages || 0,
          });
        }
      } else {
        showApiMessage(response, "fetch addons");
      }
    } catch (error) {
      logger.error("Error fetching addons:", error);
      showApiMessage(error, "fetch addons");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = pageObj => {
    const pageNumber = pageObj?.selected ?? pageObj;
    const apiPage = typeof pageNumber === "number" ? pageNumber : 1;
    fetchAddons(apiPage, pagination.size);
  };

  // Image Upload Handlers
  const handleFiles = files => {
    const validFiles = Array.from(files).filter(f => f.type.startsWith("image/"));
    const newImages = validFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
    }));
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const handleDrag = e => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = e => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = imageId => {
    setUploadedImages(prev => {
      const target = prev.find(img => img.id === imageId);
      if (target && target.url && target.url.startsWith("blob:")) {
        URL.revokeObjectURL(target.url);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const uploadPendingImages = async () => {
    const filesToUpload = uploadedImages.filter(img => img.file).map(img => img.file);
    let uploadedUrls = [];

    if (filesToUpload.length > 0) {
      try {
        const res = await spaceManagementService.uploadMedia(filesToUpload);
        if ((res.status === 200 || res.status === 201) && res.data) {
          uploadedUrls = res.data;
        }
      } catch (err) {
        logger.error("Error uploading addon media:", err);
      }
    }

    const existingUrls = uploadedImages.filter(img => !img.file).map(img => img.url);
    return [...existingUrls, ...uploadedUrls];
  };

  const buildPayloadFromForm = async () => {
    const parsedCategoryId = parseInt(formData.serviceCategoryId);
    const finalImageUrls = await uploadPendingImages();

    let availabilitySlots = [];
    if (formData.scheduleMode === "CUSTOM_SLOTS") {
      for (const dayObj of DAYS_LIST) {
        const dayConfig = formData.slotsByDay[dayObj.key];
        if (dayConfig && dayConfig.enabled) {
          const parsedStart = parseTimeString(dayConfig.startTime);
          const parsedEnd = parseTimeString(dayConfig.endTime);

          if (!dayConfig.startTime || !dayConfig.endTime) {
            throw new Error(`Please specify both start and end time for ${dayObj.label}.`);
          }
          if (parsedStart === parsedEnd) {
            throw new Error(`Start time and end time cannot be identical for ${dayObj.label}.`);
          }

          availabilitySlots.push({
            day: dayObj.key,
            startTime: parsedStart,
            endTime: parsedEnd,
          });
        }
      }
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      unitPrice: parseFloat(formData.unitPrice) || 0,
      type: formData.type,
      duration: formData.duration,
      baseDuration: parseInt(formData.baseDuration) || 1,
      maximumValidity: parseInt(formData.maximumValidity) || 0,
      numberOfInstances: parseInt(formData.numberOfInstances) || 1,
      serviceCategoryId: isNaN(parsedCategoryId) ? undefined : parsedCategoryId,
      serviceType: formData.serviceType ? formData.serviceType.trim() : undefined,
      applicableUserCategories: formData.applicableUserCategories || ["HOST", "B2B"],
      imageUrls: finalImageUrls,
      availabilitySlots,
    };

    return payload;
  };

  const handleCreateAddon = async () => {
    try {
      if (!formData.name.trim()) {
        showApiMessage({ message: "Please enter an addon name." }, "validation");
        return;
      }
      if (!formData.unitPrice || isNaN(parseFloat(formData.unitPrice))) {
        showApiMessage({ message: "Please enter a valid unit price." }, "validation");
        return;
      }

      setIsSubmitting(true);
      const payload = await buildPayloadFromForm();
      const response = await addonService.createAddon(payload);
      if (response.status === 200 || response.status === 201) {
        showApiMessage(response, "create addon");
        resetForm();
        setShowCreateForm(false);
        fetchAddons();
      } else {
        showApiMessage(response, "create addon");
      }
    } catch (error) {
      logger.error("Error creating addon:", error);
      showApiMessage(error, "create addon");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAddon = async () => {
    if (!editingAddon) return;
    try {
      if (!formData.name.trim()) {
        showApiMessage({ message: "Please enter an addon name." }, "validation");
        return;
      }

      setIsSubmitting(true);
      const payload = await buildPayloadFromForm();
      const response = await addonService.updateAddon(editingAddon.id, payload);
      if (response.status === 200) {
        showApiMessage(response, "update addon");
        resetForm();
        setEditingAddon(null);
        setShowCreateForm(false);
        fetchAddons();
      } else {
        showApiMessage(response, "update addon");
      }
    } catch (error) {
      logger.error("Error updating addon:", error);
      showApiMessage(error, "update addon");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAddon = addonId => {
    setAddonToDelete(addonId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAddon = async () => {
    if (!addonToDelete) return;
    try {
      const response = await addonService.deleteAddon(addonToDelete);
      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "delete addon");
        fetchAddons();
      } else {
        showApiMessage(response, "delete addon");
      }
      setShowDeleteConfirm(false);
      setAddonToDelete(null);
    } catch (error) {
      logger.error("Error deleting addon:", error);
      showApiMessage(error, "delete addon");
      setShowDeleteConfirm(false);
      setAddonToDelete(null);
    }
  };

  const startEdit = addon => {
    const rawSlots = addon.availabilitySlots || addon.availableSlots || [];
    const hasCustomSlots = Array.isArray(rawSlots) && rawSlots.length > 0;

    const initialSlotsByDay = { ...DEFAULT_FORM_DATA.slotsByDay };
    if (hasCustomSlots) {
      DAYS_LIST.forEach(d => {
        initialSlotsByDay[d.key] = { enabled: false, startTime: "09:00", endTime: "17:00" };
      });
      rawSlots.forEach(slot => {
        const dayKey = (slot.day || "").toUpperCase();
        if (initialSlotsByDay[dayKey]) {
          initialSlotsByDay[dayKey] = {
            enabled: true,
            startTime: formatTimeSlotDisplay(slot.startTime),
            endTime: formatTimeSlotDisplay(slot.endTime),
          };
        }
      });
    }

    const existingImgs = (addon.imageUrls || (addon.imageUrl ? [addon.imageUrl] : [])).map((url, idx) => ({
      id: `existing-${idx}`,
      url,
      name: `Image ${idx + 1}`,
      size: 0,
    }));
    setUploadedImages(existingImgs);

    setFormData({
      name: addon.name || "",
      description: addon.description || "",
      unitPrice: (addon.unitPrice ?? addon.price ?? "").toString(),
      type: addon.type || "NON_SUBSCRIPTION",
      serviceCategoryId: (addon.serviceCategoryId || addon.serviceCategory?.id || "").toString(),
      serviceType: addon.serviceType || "",
      applicableUserCategories: addon.applicableUserCategories || ["HOST", "B2B"],
      duration: addon.duration || "HOURLY",
      maximumValidity: addon.maximumValidity ?? 24,
      baseDuration: addon.baseDuration ?? 1,
      numberOfInstances: addon.numberOfInstances ?? 1,
      scheduleMode: hasCustomSlots ? "CUSTOM_SLOTS" : "ALL_SLOTS",
      slotsByDay: initialSlotsByDay,
    });
    setEditingAddon(addon);
    setShowCreateForm(true);
  };

  const resetForm = () => {
    setFormData(DEFAULT_FORM_DATA);
    setUploadedImages([]);
    setEditingAddon(null);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDaySlotToggle = (dayKey, enabled) => {
    setFormData(prev => ({
      ...prev,
      slotsByDay: {
        ...prev.slotsByDay,
        [dayKey]: { ...prev.slotsByDay[dayKey], enabled },
      },
    }));
  };

  const handleDayTimeChange = (dayKey, timeField, value) => {
    setFormData(prev => ({
      ...prev,
      slotsByDay: {
        ...prev.slotsByDay,
        [dayKey]: { ...prev.slotsByDay[dayKey], [timeField]: value },
      },
    }));
  };

  return (
    <>
      <h2 className="mx-3 mb-4 text-xl font-semibold text-gray-800">Addons</h2>

      <div className="p-6 mx-3 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-5">
        {/* Header with Add Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Available Addons</h3>
            <p className="text-sm text-gray-500 mt-0.5">Manage extra amenities, equipment, and booking options for your spaces.</p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center"
          >
            <Plus size={16} className="mr-2" />
            <span>Add New Addon</span>
          </Button>
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
          <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editingAddon ? "Edit Addon" : "Create New Addon"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {editingAddon
                  ? "Update the details and availability of your addon."
                  : "Add a new addon to your organization."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-5 py-2">
              {/* Basic Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="addon-name">Name *</Label>
                  <Input
                    id="addon-name"
                    value={formData.name}
                    onChange={e => handleInputChange("name", e.target.value)}
                    placeholder="e.g. Smart Whiteboard Access"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addon-category">Service Category *</Label>
                  <Select
                    value={formData.serviceCategoryId}
                    onValueChange={value => handleInputChange("serviceCategoryId", value)}
                    disabled={isLoadingCategories}
                  >
                    <SelectTrigger id="addon-category">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceCategories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addon-service-type">Service Type</Label>
                  <Input
                    id="addon-service-type"
                    value={formData.serviceType}
                    onChange={e => handleInputChange("serviceType", e.target.value)}
                    placeholder="e.g. AV Equipment, Catering"
                  />
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="addon-description">Description</Label>
                  <Textarea
                    id="addon-description"
                    value={formData.description}
                    onChange={e => handleInputChange("description", e.target.value)}
                    rows={2}
                    placeholder="Describe what is included with this addon..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addon-price">Unit Price ({userCurrency}) *</Label>
                  <Input
                    id="addon-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unitPrice}
                    onChange={e => handleInputChange("unitPrice", e.target.value)}
                    placeholder="15.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addon-duration">Duration</Label>
                  <Select value={formData.duration} onValueChange={value => handleInputChange("duration", value)}>
                    <SelectTrigger id="addon-duration">
                      <SelectValue placeholder="per hour" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HOURLY">per hour</SelectItem>
                      <SelectItem value="DAILY">per day</SelectItem>
                      <SelectItem value="WEEKLY">per week</SelectItem>
                      <SelectItem value="MONTHLY">per month</SelectItem>
                      <SelectItem value="FIXED">One-time / Fixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addon-instances">Number of Instances</Label>
                  <Input
                    id="addon-instances"
                    type="number"
                    value={formData.numberOfInstances}
                    onChange={e => handleInputChange("numberOfInstances", parseInt(e.target.value) || 1)}
                    min="1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addon-validity">Maximum Validity (hours)</Label>
                  <Input
                    id="addon-validity"
                    type="number"
                    value={formData.maximumValidity}
                    onChange={e => handleInputChange("maximumValidity", parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>
              </div>

              {/* Drag and Drop Image Upload Section */}
              <div className="space-y-2 pt-2 border-t">
                <div className="flex justify-between items-center">
                  <Label>Addon Images</Label>
                  <span className="text-xs text-gray-500">{uploadedImages.length} image(s)</span>
                </div>

                <div
                  className={`border-2 border-dashed border-gray-300 rounded-lg p-5 text-center hover:border-gray-400 transition-colors cursor-pointer ${
                    dragActive ? "border-blue-500 bg-blue-50" : ""
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    style={{ display: "none" }}
                  />
                  <svg
                    className="mx-auto h-7 w-7 text-gray-400 mb-1.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-gray-600 text-sm mb-1">Drag and drop addon images here</p>
                  <span className="text-blue-600 hover:text-blue-700 text-xs font-medium">Browse Files</span>
                  <p className="text-[11px] text-gray-400 mt-1">PNG, JPG, WEBP up to 5MB</p>
                </div>

                {/* Previews */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 pt-1">
                    {uploadedImages.map(image => (
                      <div key={image.id} className="relative group rounded-lg overflow-hidden border border-gray-200">
                        <img src={image.url} alt={image.name} className="w-full h-16 object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(image.id)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-80 hover:opacity-100 transition-opacity shadow-xs"
                          title="Remove image"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Operating Schedule Configuration */}
              <div className="space-y-3 pt-2 border-t">
                <div className="flex justify-between items-center">
                  <Label>Availability Schedule</Label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    onClick={() => handleInputChange("scheduleMode", "ALL_SLOTS")}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.scheduleMode === "ALL_SLOTS"
                        ? "border-blue-600 bg-blue-50/50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">Always Available</div>
                    <div className="text-xs text-gray-500 mt-0.5">Available with any space slot</div>
                  </div>

                  <div
                    onClick={() => handleInputChange("scheduleMode", "CUSTOM_SLOTS")}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      formData.scheduleMode === "CUSTOM_SLOTS"
                        ? "border-blue-600 bg-blue-50/50"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <div className="font-medium text-sm text-gray-900">Custom Operating Hours</div>
                    <div className="text-xs text-gray-500 mt-0.5">Specify active days and hours</div>
                  </div>
                </div>

                {formData.scheduleMode === "CUSTOM_SLOTS" && (
                  <div className="space-y-2 pt-1">
                    {DAYS_LIST.map(dayObj => {
                      const slot = formData.slotsByDay[dayObj.key] || {
                        enabled: false,
                        startTime: "09:00",
                        endTime: "17:00",
                      };
                      return (
                        <div
                          key={dayObj.key}
                          className="flex items-center justify-between p-2 rounded-md border border-gray-200 bg-gray-50/50"
                        >
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id={`slot-${dayObj.key}`}
                              checked={slot.enabled}
                              onCheckedChange={checked => handleDaySlotToggle(dayObj.key, checked)}
                            />
                            <Label htmlFor={`slot-${dayObj.key}`} className="text-xs font-medium cursor-pointer">
                              {dayObj.label}
                            </Label>
                          </div>

                          {slot.enabled ? (
                            <div className="flex items-center space-x-2">
                              <Input
                                type="time"
                                value={slot.startTime}
                                onChange={e => handleDayTimeChange(dayObj.key, "startTime", e.target.value)}
                                className="h-7 w-24 text-xs"
                              />
                              <span className="text-xs text-gray-400">to</span>
                              <Input
                                type="time"
                                value={slot.endTime}
                                onChange={e => handleDayTimeChange(dayObj.key, "endTime", e.target.value)}
                                className="h-7 w-24 text-xs"
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400 italic">Unavailable</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="flex justify-end space-x-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={editingAddon ? handleUpdateAddon : handleCreateAddon}
                disabled={isSubmitting}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isSubmitting ? "Saving..." : editingAddon ? "Update Addon" : "Create Addon"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Addon List */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="flex items-center gap-4 p-4 border border-slate-50 rounded-2xl animate-pulse">
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="h-4 w-36 bg-slate-100 rounded" />
                  <div className="h-3 w-56 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : addons.length === 0 ? (
          <div className="py-12 text-center">
            <Package className="mx-auto w-12 h-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No addons</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first addon.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {addons.map(addon => {
              const thumbnail = (addon.imageUrls && addon.imageUrls[0]) || addon.imageUrl;
              const rawSlots = addon.availabilitySlots || addon.availableSlots;
              const hasCustomSlots = Array.isArray(rawSlots) && rawSlots.length > 0;

              return (
                <div key={addon.id} className="p-4 rounded-lg border border-gray-200 transition hover:shadow-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-3 flex-1 min-w-0">
                      {thumbnail && (
                        <img
                          src={thumbnail}
                          alt={addon.name}
                          className="w-16 h-16 rounded-lg object-cover border border-gray-200 shrink-0"
                        />
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex gap-3 items-center mb-2 flex-wrap">
                          <h4 className="font-medium text-gray-900 text-md">{addon.name}</h4>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              addon.type === "SUBSCRIPTION"
                                ? "bg-green-100 text-green-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {addon.type?.replace("_", " ") || "ONE-TIME"}
                          </span>
                          {addon.duration && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-700">
                              {addon.duration}
                            </span>
                          )}
                        </div>

                        {addon.description && (
                          <p className="mb-3 text-sm text-gray-600 line-clamp-2">{addon.description}</p>
                        )}

                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <div className="flex gap-1 items-center font-medium text-gray-900">
                            <Tag className="w-4 h-4 text-blue-600" />
                            <span>
                              {userCurrency} {(addon.unitPrice ?? addon.price ?? 0).toLocaleString()}{" "}
                              {formatDurationType(addon.duration)}
                            </span>
                          </div>
                          {typeof addon.numberOfInstances === "number" && (
                            <div className="flex gap-1 items-center">
                              <Package className="w-4 h-4" />
                              <span>{addon.numberOfInstances} instances</span>
                            </div>
                          )}
                          <div className="flex gap-1 items-center">
                            <Clock className="w-4 h-4" />
                            <span>{hasCustomSlots ? "Custom Schedule" : "Always Available"}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startEdit(addon)}
                        className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteAddon(addon.id)}
                        className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Addon</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete this addon? This action cannot be undone.
              </p>
            </div>
            <DialogFooter className="flex justify-end space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setAddonToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={confirmDeleteAddon} className="bg-[#3A36DB] hover:bg-[#2d2bb8] text-white">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pagination */}
        {addons.length > 0 && pagination.totalElements > pagination.size && (
          <div className="flex justify-center mt-6">
            <Pagination
              className="pagination-bar"
              currentPage={pagination.page}
              totalCount={pagination.totalElements}
              pageSize={pagination.size}
              siblingCount={1}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AddonsSettings;
